import { BillingPreferencesForm } from '@features/billing-preferences/billing-preferences-form.ts';
import { ElectricBoltOutlined, WaterDropOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { Service } from '@/types/api.ts';
import { formatCurrency } from '@/utils/currency.ts';

/**
 * Billing preference selection for each service on the application.
 */
export const BillingPreferencesSelection = ({ availableServices }: { availableServices: Service[] }) => {
  const { control } = useFormContext<BillingPreferencesForm>();

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Choose Your Billing Preferences
      </Typography>
      <Typography color="text.secondary" variant="body2" marginBottom={2}>
        Select your billing preferences for each of the services.
      </Typography>
      <Stack spacing={2}>
        {availableServices.map((service, index) => {
          return (
            <Card variant="outlined" key={service.id}>
              <CardContent>
                <Controller
                  control={control}
                  name={`services.${index}.id`}
                  defaultValue={service.id}
                  render={({ field }) => (
                    <input type="hidden" {...field} />
                  )}
                />
                <Stack direction="row" gap={1}>
                  {service.type === 'electric' && (
                    <ElectricBoltOutlined color="warning" />
                  )}
                  {service.type === 'water' && (
                    <WaterDropOutlined color="info" />
                  )}
                  <Stack>
                    <Typography variant="body1">
                      {service.label}
                      {' '}
                      Service
                    </Typography>
                    <Typography variant="body2">{service.description}</Typography>
                  </Stack>
                </Stack>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name={`services.${index}.billingPreference`}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <FormControlLabel
                            value="standard"
                            control={<Radio />}
                            label="Standard Billing"
                          />
                          <Typography variant="body1" color="text.primary">
                            {formatCurrency(service.standardTotal)}
                          </Typography>
                        </Stack>
                        {service.isPreferenceEditable && (
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <FormControlLabel
                              value="prepaid"
                              control={<Radio />}
                              label="Prepaid Billing"
                            />
                            <Typography variant="body1" color="text.primary">
                              {formatCurrency(service.prepaidTotal)}
                            </Typography>
                          </Stack>
                        )}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};
